import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  getExportUrl,
  startImport,
  getImportJob,
  type ImportJobResponse,
} from "@/services/adminApi";
import apiFetch from "@/app/auth/apiFetch";
import { Download, Upload, Loader2, CheckCircle, XCircle, FileUp } from "lucide-react";

export default function DataTransferPage() {
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importJob, setImportJob] = useState<ImportJobResponse | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 8000);
  };

  // Export
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiFetch(getExportUrl(), { method: "GET" }, navigate);
      if (!res?.ok) throw new Error("Erreur export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recrutauto-export-${new Date().toISOString().slice(0, 10)}.ndjson`;
      a.click();
      URL.revokeObjectURL(url);
      showMessage("success", "Export termine");
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setExporting(false);
    }
  };

  // Import
  const handleImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const res = await startImport(navigate, importFile);
      setImportJob({
        job_id: res.job_id,
        status: "pending",
        processed: 0,
        total: res.total,
        started_at: new Date().toISOString(),
        skills_added: 0, skills_existing: 0,
        industries_added: 0, industries_existing: 0,
        companies_added: 0, companies_updated: 0, companies_skipped: 0,
        schools_added: 0, schools_updated: 0, schools_skipped: 0,
        candidates_added: 0, candidates_updated: 0, candidates_skipped: 0,
      } as ImportJobResponse);
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setImportLoading(false);
    }
  };

  // Poll import job
  const refreshJob = useCallback(async () => {
    if (!importJob?.job_id) return false;
    try {
      const job = await getImportJob(navigate, importJob.job_id);
      setImportJob(job);
      return job.status === "pending" || job.status === "running";
    } catch {
      return false;
    }
  }, [importJob?.job_id, navigate]);

  useEffect(() => {
    const isActive = importJob && (importJob.status === "pending" || importJob.status === "running");

    if (isActive && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const stillActive = await refreshJob();
        if (!stillActive && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 2000);
    }

    if (!isActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // Intentionally keyed on `importJob?.status` (not the whole `importJob` object,
    // which is replaced on every poll tick) so the interval is only torn down /
    // recreated on a real status transition, not every 2s.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importJob?.status, refreshJob]);

  const pct = importJob && importJob.total > 0 ? Math.round((importJob.processed / importJob.total) * 100) : 0;
  const isDone = importJob && (importJob.status === "completed" || importJob.status === "failed");

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Transfert de donnees</h1>
          </div>
        </header>

        <div className="p-6 space-y-6 max-w-3xl">
          {message && (
            <div className={`px-4 py-3 rounded text-sm ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
              {message.text}
            </div>
          )}

          {/* Export */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Download className="h-4 w-4" /> Exporter les donnees scrapees
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Exporte toutes les donnees scrapees (candidats, entreprises, ecoles, competences) au format NDJSON.
                Ce fichier peut etre importe sur un autre environnement.
              </p>
              <Button onClick={handleExport} disabled={exporting}>
                {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                {exporting ? "Export en cours..." : "Telecharger l'export"}
              </Button>
            </CardContent>
          </Card>

          {/* Import */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Upload className="h-4 w-4" /> Importer des donnees
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Importe un fichier NDJSON exporte depuis un autre environnement.
                Les donnees deja presentes et plus recentes ne seront pas ecrasees.
              </p>

              <div className="flex items-center gap-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".ndjson,.jsonl"
                  className="hidden"
                  onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                />
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importLoading || (!!importJob && !isDone)}>
                  <FileUp className="h-4 w-4 mr-2" />
                  {importFile ? importFile.name : "Choisir un fichier"}
                </Button>
                <Button onClick={handleImport} disabled={!importFile || importLoading || (!!importJob && !isDone)}>
                  {importLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Importer
                </Button>
              </div>

              {/* Progress */}
              {importJob && (
                <div className="space-y-3 border rounded p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium flex items-center gap-1.5">
                      {importJob.status === "completed" && <CheckCircle className="h-4 w-4 text-green-600" />}
                      {importJob.status === "failed" && <XCircle className="h-4 w-4 text-red-600" />}
                      {(importJob.status === "running" || importJob.status === "pending") && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                      {importJob.status === "completed" ? "Import termine" :
                       importJob.status === "failed" ? "Import echoue" :
                       "Import en cours..."}
                    </span>
                    <span className="text-muted-foreground font-mono">{importJob.processed}/{importJob.total}</span>
                  </div>
                  <Progress value={pct} className="h-2" />

                  {importJob.error && (
                    <p className="text-sm text-red-600">{importJob.error}</p>
                  )}

                  {/* Summary */}
                  {(importJob.status === "completed" || importJob.processed > 0) && (
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div className="border rounded p-2">
                        <div className="font-semibold mb-1">Competences</div>
                        <div className="text-green-600">+{importJob.skills_added} nouvelles</div>
                        <div className="text-muted-foreground">{importJob.skills_existing} existantes</div>
                      </div>
                      <div className="border rounded p-2">
                        <div className="font-semibold mb-1">Entreprises</div>
                        <div className="text-green-600">+{importJob.companies_added} nouvelles</div>
                        <div className="text-blue-600">{importJob.companies_updated} mises a jour</div>
                        <div className="text-muted-foreground">{importJob.companies_skipped} ignorees</div>
                      </div>
                      <div className="border rounded p-2">
                        <div className="font-semibold mb-1">Ecoles</div>
                        <div className="text-green-600">+{importJob.schools_added} nouvelles</div>
                        <div className="text-blue-600">{importJob.schools_updated} mises a jour</div>
                        <div className="text-muted-foreground">{importJob.schools_skipped} ignorees</div>
                      </div>
                      <div className="border rounded p-2 col-span-2">
                        <div className="font-semibold mb-1">Candidats</div>
                        <div className="text-green-600">+{importJob.candidates_added} nouveaux</div>
                        <div className="text-blue-600">{importJob.candidates_updated} mis a jour</div>
                        <div className="text-muted-foreground">{importJob.candidates_skipped} ignores</div>
                      </div>
                      <div className="border rounded p-2">
                        <div className="font-semibold mb-1">Industries</div>
                        <div className="text-green-600">+{importJob.industries_added} nouvelles</div>
                        <div className="text-muted-foreground">{importJob.industries_existing} existantes</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
